import { Component } from '@theme/component';

/**
 * Countdown timer for the announcement bar.
 * The end time is stored in sessionStorage, so the timer survives page changes and reloads
 * within a browser session and restarts with the configured duration in a new session.
 * All bars share the key "store", so they show the same time across every page.
 * Once it reaches zero it stays at 00:00:00.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 * @property {HTMLElement} seconds
 *
 * @extends {Component<Refs>}
 */
class CountdownBar extends Component {
  requiredRefs = ['hours', 'minutes', 'seconds'];

  /** @type {number|undefined} */
  #interval;

  /** @type {number} */
  #endTime = 0;

  connectedCallback() {
    super.connectedCallback();

    const duration = Number(this.dataset.duration) * 1000;
    this.#endTime = this.dataset.reset === 'pageload' ? Date.now() + duration : this.#getSessionEndTime(duration);

    this.#tick();
    this.#interval = window.setInterval(this.#tick, 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.#interval);
  }

  /**
   * @param {number} duration - Countdown duration in milliseconds.
   * @returns {number} The end timestamp for the current session.
   */
  #getSessionEndTime(duration) {
    const key = `countdown-bar:${this.dataset.key}`;

    try {
      const stored = Number(sessionStorage.getItem(key));
      if (stored > 0) return stored;

      const endTime = Date.now() + duration;
      sessionStorage.setItem(key, String(endTime));
      return endTime;
    } catch {
      return Date.now() + duration;
    }
  }

  #tick = () => {
    const remaining = Math.max(0, Math.round((this.#endTime - Date.now()) / 1000));
    const { hours, minutes, seconds } = this.refs;

    hours.textContent = String(Math.floor(remaining / 3600)).padStart(2, '0');
    minutes.textContent = String(Math.floor((remaining % 3600) / 60)).padStart(2, '0');
    seconds.textContent = String(remaining % 60).padStart(2, '0');

    if (remaining === 0) {
      window.clearInterval(this.#interval);
      this.dataset.expired = '';
    }
  };
}

if (!customElements.get('countdown-bar')) {
  customElements.define('countdown-bar', CountdownBar);
}
